<hlm-dialog [state]="state()" (closed)="close()" class="[&>brn-dialog-overlay]:!bg-black/60">
  <hlm-dialog-content *brnDialogContent="let ctx" class="sm:max-w-[620px]">
    <hlm-dialog-header class="pb-4 pr-6">
      <p class="font-mono text-2xs font-bold uppercase tracking-[0.08em] text-muted-foreground">
        {{ taskRangeLabel() }}
      </p>
      <h2 hlmDialogTitle class="text-xl font-bold tracking-tight">Ausführung vorbereiten</h2>
      <p hlmDialogDescription class="text-sm text-muted-foreground mt-1">
        Prüfen Sie die Eingaben und starten Sie anschließend die ausgewählten Aufgaben.
      </p>
    </hlm-dialog-header>

    <div class="py-2 -mx-6 px-6 max-h-[60vh] overflow-y-auto space-y-4">
      <!-- Error -->
      @if (error()) {
        <div hlmAlert variant="destructive" class="mb-2">
          <ng-icon hlmAlertIcon name="lucideTriangleAlert" />
          <h4 hlmAlertTitle class="text-sm">
            @if (errorType() === "invalid-state") { Ungültiger Status } @else { Fehler bei der Auswahl }
          </h4>
          <p hlmAlertDescription class="text-xs">{{ error() }}</p>
          @if (errorType() === "network") {
            <button hlmBtn variant="outline" size="sm" class="mt-3 text-xs" (click)="retry()" [disabled]="isLoading()">
              <ng-icon name="lucideRefreshCw" size="14" class="mr-1.5" />
              Erneut versuchen
            </button>
          }
        </div>
      }

      @if (requiresMedia()) {
        <section class="space-y-3">
          <div>
            <p class="font-mono text-2xs font-bold uppercase tracking-[0.08em] text-muted-foreground">
              Eingabe · Analyseformat
            </p>
            <h3 class="mt-1 text-sm font-bold text-foreground">
              @if (prospective()) { 2D oder 3D auswählen } @else { Analysedatei auswählen }
            </h3>
          </div>

          @if (prospective()) {
            <app-analysis-type-picker
              [selected]="selectedMediaType()"
              [showAppliedBadge]="false"
              [disabled]="isLoading()"
              (typeSelected)="selectProspectiveType($event)"
            />
          }

      <!-- 2D files -->
      @if (!prospective() && items2D().length > 0) {
        <div class="space-y-1.5">
          <div class="flex items-center gap-2 mb-2">
            <div class="w-6 h-6 rounded bg-success-soft text-success flex items-center justify-center flex-shrink-0">
              <ng-icon name="lucideImage" size="13" />
            </div>
            <span class="text-xs font-bold text-muted-foreground uppercase tracking-wide">2D Bilder</span>
          </div>

          @for (item of items2D(); track item.id) {
            <button
              type="button"
              class="w-full flex items-center gap-3 rounded-md border p-3 text-left transition-colors"
              [ngClass]="{
                'border-primary bg-primary/5': selectedMediaId() === item.id,
                'hover:bg-muted/40 border-border': selectedMediaId() !== item.id
              }"
              (click)="selectFile(item)"
            >
              <div class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border bg-muted/50 text-muted-foreground">
                <ng-icon [name]="item.icon" size="15" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="text-sm font-normal">{{ item.label }}</span>
                  @if (selectedMediaId() === item.id) {
                    <span class="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs font-normal text-primary">
                      <ng-icon name="lucideCircleCheck" size="11" />
                      Ausgewählt
                    </span>
                  }
                </div>
                <p class="text-xs text-muted-foreground truncate">
                  {{ item.filename }}
                  @if (item.size) { <span class="mx-1">·</span>{{ item.size }} }
                  @if (item.date) { <span class="mx-1">·</span>{{ item.date }} }
                </p>
              </div>
            </button>
          }
        </div>
      }

      <!-- 3D files -->
      @if (!prospective() && items3D().length > 0) {
        <div class="space-y-1.5">
          <div class="flex items-center gap-2 mb-2">
            <div class="w-6 h-6 rounded bg-secondary text-muted-foreground flex items-center justify-center flex-shrink-0">
              <ng-icon name="lucideBox" size="13" />
            </div>
            <span class="text-xs font-bold text-muted-foreground uppercase tracking-wide">3D Modelle</span>
          </div>

          @for (item of items3D(); track item.id) {
            <button
              type="button"
              class="w-full flex items-center gap-3 rounded-md border p-3 text-left transition-colors"
              [ngClass]="{
                'border-primary bg-primary/5': selectedMediaId() === item.id,
                'hover:bg-muted/40 border-border': selectedMediaId() !== item.id
              }"
              (click)="selectFile(item)"
            >
              <div class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border bg-muted/50 text-muted-foreground">
                <ng-icon name="lucideBox" size="15" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="text-sm font-normal">{{ item.label }}</span>
                  @if (selectedMediaId() === item.id) {
                    <span class="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs font-normal text-primary">
                      <ng-icon name="lucideCircleCheck" size="11" />
                      Ausgewählt
                    </span>
                  }
                </div>
                <p class="text-xs text-muted-foreground truncate">
                  {{ item.filename }}
                  @if (item.size) { <span class="mx-1">·</span>{{ item.size }} }
                  @if (item.date) { <span class="mx-1">·</span>{{ item.date }} }
                </p>
              </div>
            </button>
          }
        </div>
      }

      <!-- Empty state -->
      @if (!prospective() && hasNoFiles()) {
        <div class="flex flex-col items-center justify-center py-10 text-center text-muted-foreground">
          <ng-icon name="lucideFile" size="32" class="mb-3 opacity-40" />
          <p class="text-sm">Keine Dateien verfügbar</p>
          <p class="text-xs mt-1">Für diese Analyse wurden noch keine Dateien hochgeladen.</p>
        </div>
      }
        </section>
      }

      @if (requiresTemplate()) {
        <section class="space-y-3 border-t border-border pt-4">
          <div>
            <p class="font-mono text-2xs font-bold uppercase tracking-[0.08em] text-muted-foreground">
              Eingabe · Planung
            </p>
            <h3 class="mt-1 text-sm font-bold text-foreground">Planungsvorlage auswählen</h3>
          </div>

          <div class="grid gap-3 sm:grid-cols-3">
            <label class="space-y-1.5 text-xs text-muted-foreground">
              <span>Name</span>
              <input
                type="text"
                class="h-9 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none focus:border-primary"
                [ngModel]="planningName()"
                (ngModelChange)="planningName.set($event)"
                [disabled]="isLoading()"
              />
            </label>
            <label class="space-y-1.5 text-xs text-muted-foreground">
              <span>Jahresverbrauch · kWh</span>
              <input
                type="number"
                min="1"
                class="h-9 w-full rounded-md border border-border bg-background px-3 text-sm tabular-nums text-foreground outline-none focus:border-primary"
                [ngModel]="planningPowerConsumption()"
                (ngModelChange)="setPlanningPowerConsumption($event)"
                [disabled]="isLoading()"
              />
            </label>
            <label class="space-y-1.5 text-xs text-muted-foreground">
              <span>Strompreis · €/kWh</span>
              <input
                type="number"
                min="0.01"
                step="0.0001"
                class="h-9 w-full rounded-md border border-border bg-background px-3 text-sm tabular-nums text-foreground outline-none focus:border-primary"
                [ngModel]="planningElectricityPrice()"
                (ngModelChange)="setPlanningElectricityPrice($event)"
                [disabled]="isLoading()"
              />
            </label>
          </div>

          @if (isLoadingTemplates()) {
            <div class="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <ng-icon name="lucideLoader" size="16" class="animate-spin" />
              Vorlagen werden geladen…
            </div>
          } @else if (templates().length === 0) {
            <div class="rounded-xl border border-dashed border-border px-4 py-8 text-center">
              <ng-icon name="lucideLayoutTemplate" size="28" class="mx-auto text-muted-foreground/40" />
              <p class="mt-2 text-sm text-muted-foreground">Keine Planungsvorlagen vorhanden.</p>
            </div>
          } @else {
            <div class="space-y-1.5">
              @for (template of templates(); track template.id) {
                <button
                  type="button"
                  class="flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors"
                  [ngClass]="{
                    'border-primary bg-primary/5': selectedTemplateId() === template.id,
                    'border-border hover:bg-muted/40': selectedTemplateId() !== template.id
                  }"
                  [disabled]="isLoading()"
                  (click)="selectTemplate(template.id)"
                >
                  <div class="flex size-8 shrink-0 items-center justify-center rounded-lg border bg-muted/50 text-muted-foreground">
                    <ng-icon name="lucideLayoutTemplate" size="15" />
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="flex flex-wrap items-center gap-2">
                      <span class="text-sm font-normal">{{ template.name }}</span>
                      @if (template.type) {
                        <span hlmBadge variant="secondary" class="text-xs">{{ getTemplateTypeLabel(template.type) }}</span>
                      }
                      @if (selectedTemplateId() === template.id) {
                        <span class="inline-flex items-center gap-1 text-xs font-normal text-primary">
                          <ng-icon name="lucideCircleCheck" size="11" /> Ausgewählt
                        </span>
                      }
                    </div>
                    <p class="mt-0.5 truncate text-xs text-muted-foreground">
                      {{ template.description || (template.calculationPeriod + ' Jahre') }}
                    </p>
                  </div>
                </button>
              }
            </div>
          }
        </section>
      }

      @if (requiresSolarOverride()) {
        <section class="border-t border-border pt-4">
          <label class="flex cursor-pointer items-start gap-2.5 rounded-lg px-1 py-1 text-sm text-foreground">
            <input
              type="checkbox"
              class="mt-0.5 size-4 accent-primary"
              [ngModel]="ignoreExistingSolar()"
              (ngModelChange)="ignoreExistingSolar.set($event)"
              [disabled]="isLoading()"
            />
            <span>
              <span class="font-normal">Bestehende Solaranlage ignorieren</span>
              <span class="mt-0.5 block text-xs text-muted-foreground">
                Erforderlich, um die Planung trotzdem zu starten.
              </span>
            </span>
          </label>
        </section>
      }

    </div>

    <hlm-dialog-footer class="pt-4 gap-3">
      <button hlmBtn variant="outline" (click)="close()" class="text-sm">Abbrechen</button>
      <button hlmBtn [disabled]="!canConfirm() || isLoading()" (click)="confirm()" class="text-sm gap-2">
        @if (isLoading()) {
          <ng-icon name="lucideLoader" size="14" class="animate-spin" />
          Wird übernommen…
        } @else {
          <ng-icon name="lucidePlay" size="14" />
          Ausführung starten
        }
      </button>
    </hlm-dialog-footer>
  </hlm-dialog-content>
</hlm-dialog>
